import React from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  InputBase,
  Badge,
  Avatar,
  useMediaQuery,
  useTheme,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Notifications as NotificationsIcon,
  AccountCircle as AccountCircleIcon,
  Menu as MenuIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useAppTheme } from '../../contexts/ThemeContext';

const Search = styled('div', {
  shouldForwardProp: (prop) => !['navPalette'].includes(prop),
})(({ navPalette }) => ({
  position: 'relative',
  borderRadius: '24px',
  backgroundColor: navPalette.searchBg,
  border: `1px solid transparent`,
  '&:hover': {
    backgroundColor: navPalette.searchHoverBg,
  },
  '&:focus-within': {
    backgroundColor: navPalette.searchFocusBg,
    border: `1px solid ${navPalette.icon}40`,
  },
  marginRight: 16,
  width: '100%',
  maxWidth: '500px',
  height: '46px',
  display: 'flex',
  alignItems: 'center',
  transition: 'all 0.2s ease',
}));

const SearchIconWrapper = styled('div')({
  padding: '0 16px',
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const StyledInputBase = styled(InputBase, {
  shouldForwardProp: (prop) => prop !== 'textColor',
})(({ textColor }) => ({
  color: textColor,
  width: '100%',
  height: '100%',
  '& .MuiInputBase-input': {
    padding: '8px 8px 8px 0',
    paddingLeft: 'calc(1em + 32px)',
    fontSize: '0.9rem',
    fontWeight: 400,
    '&::placeholder': {
      color: textColor,
      opacity: 0.5,
    },
  },
}));

const Navbar = ({ isCollapsed = false, onMenuClick }) => {
  const muiTheme   = useTheme();
  const isMobile   = useMediaQuery(muiTheme.breakpoints.down('md'));
  const { palette, mode, toggleMode } = useAppTheme();
  const nav = palette.navbar;

  const sidebarWidth = isMobile ? 0 : (isCollapsed ? 80 : 240);

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        zIndex: (t) => t.zIndex.drawer + 1,
        height: '80px',
        width: isMobile ? '100%' : `calc(100% - ${sidebarWidth}px)`,
        marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
        transition: muiTheme.transitions.create(['width', 'margin-left'], {
          easing: muiTheme.transitions.easing.sharp,
          duration: muiTheme.transitions.duration.enteringScreen,
        }),
        backgroundColor: nav.background,
        backgroundImage: 'none',
        boxShadow: 'none',
        borderBottom: `1px solid ${nav.border}`,
      }}
    >
      <Toolbar
        sx={{
          height: '100%',
          px: isMobile ? 2 : 3,
          display: 'flex',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        {/* Left: hamburger (mobile) + search */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
          {isMobile && (
            <IconButton
              onClick={onMenuClick}
              sx={{
                color: nav.icon,
                '&:hover': { backgroundColor: `${nav.icon}10` },
              }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Search navPalette={nav}>
            <SearchIconWrapper>
              <SearchIcon sx={{ color: palette.text.muted, fontSize: 20 }} />
            </SearchIconWrapper>
            <StyledInputBase
              placeholder="Search..."
              textColor={palette.text.primary}
              inputProps={{ 'aria-label': 'search' }}
            />
          </Search>
        </Box>

        {/* Right: theme toggle + notifications + avatar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>

          {/* Theme toggle */}
          <Tooltip title={mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}>
            <IconButton
              onClick={toggleMode}
              sx={{
                color: nav.icon,
                '&:hover': { backgroundColor: `${nav.icon}15` },
              }}
            >
              {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
            </IconButton>
          </Tooltip>

          {/* Notifications */}
          <IconButton
            sx={{
              color: nav.icon,
              '&:hover': { backgroundColor: `${nav.icon}15` },
            }}
          >
            <Badge color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>

          {/* Avatar */}
          <IconButton sx={{ p: 0.5 }}>
            <Avatar sx={{ width: 38, height: 38, bgcolor: nav.icon }}>
              <AccountCircleIcon />
            </Avatar>
          </IconButton>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;